// Revisado: 2025-12-18 [I18n Scan]
import React, { useState, useEffect, useRef } from 'react';
import { calculateStatScore } from '../../../Calculations/StatsScoreCalculator';
import { useTranslation } from 'react-i18next';
import { getRank, getRankColor } from '../../../../utils/statUtils';


// --- COMPONENT: StatItem ---
const StatItem = ({ name, value, isLast, onStatChange, iconUrl, t }) => {
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);

  // Resize observer for the rainbow bar mask
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  const handleBlur = (e) => {
    const text = e.target.innerText;
    let numericValue = parseInt(text, 10) || 0;

    // Cap Logic
    if (numericValue > 2000) numericValue = 2000;

    if (onStatChange) onStatChange(name, numericValue);

    // Reset text if it was non-numeric or capped
    if (e.target.innerText !== numericValue.toString()) {
      e.target.innerText = numericValue;
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.target.blur();
    }
  };

  const handleFocus = (e) => {
    // Select all text on focus so user can overwrite easily
    setTimeout(() => {
      const range = document.createRange();
      range.selectNodeContents(e.target);
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
    }, 0);
  };

  // Calculate Bar Widths
  const maxGreen = 1200;
  const maxRainbow = 2000;
  const rainbowRange = maxRainbow - maxGreen;

  // Green fills up to 1200
  const greenPercentage = Math.min(Math.max(((value || 0) / maxGreen) * 100, 0), 100);

  // Rainbow starts filling after 1200
  const rainbowPercentage = (value || 0) <= maxGreen
    ? 0
    : Math.min(Math.max((((value || 0) - maxGreen) / rainbowRange) * 100, 0), 100);

  const rank = getRank(value || 0);
  const rankColorClass = getRankColor(rank);

  // Display Name logic (Translated)
  const displayName = t ? t(`StatPanel.stats.${name}`) : name;
  const shortName = displayName.substring(0, 3);
  const initial = displayName.charAt(0);

  return (
    <div className={`flex-1 flex flex-col relative ${!isLast ? 'border-r border-gray-200 dark:border-slate-700' : ''} transition-colors duration-300`}>

      {/* Header with Icon */}
      <div className="bg-[#00c541] dark:bg-[#00a035] text-white py-2 flex items-center justify-center gap-2 shadow-sm z-20 transition-colors duration-300">
        <img
          src={iconUrl}
          alt={`${name} icon`}
          className="w-5 h-5 object-contain drop-shadow-sm filter brightness-110"
          draggable="false"
        />
        <span className="font-black uppercase text-[10px] sm:text-xs tracking-widest select-none text-shadow-sm hidden sm:inline-block">
          {shortName}
        </span>
        <span className="font-black uppercase text-[10px] sm:text-xs tracking-widest select-none text-shadow-sm sm:hidden">
          {initial}
        </span>
      </div>

      {/* Editable Body */}
      <div className="bg-white dark:bg-slate-800 flex-grow py-5 relative flex flex-col items-center justify-center overflow-hidden group transition-colors duration-300 min-h-[120px]">

        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-[0.03] dark:opacity-[0.05] pointer-events-none"
          style={{ backgroundImage: 'radial-gradient(circle, currentColor 1px, transparent 1px)', backgroundSize: '12px 12px' }}>
        </div>

        {/* Watermark Rank (Background Letter) */}
        <div className={`absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-5xl sm:text-7xl font-black select-none pointer-events-none transition-colors duration-500 ${rankColorClass}`}>
          {rank}
        </div>

        {/* Numeric Input */}
        <span
          contentEditable={true}
          suppressContentEditableWarning={true}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          key={value} // Force re-render on external update
          className="relative z-10 text-xl sm:text-3xl font-black text-gray-800 dark:text-gray-100 focus:outline-none focus:text-[#00c541] dark:focus:text-[#4ade80] transition-all duration-200 cursor-text font-mono drop-shadow-sm"
        >
          {value || 0}
        </span>

        {/* Progress Bar Container */}
        <div className="w-full px-2 sm:px-4 mt-3 absolute bottom-3 z-20">
          <div ref={containerRef} className="w-full h-2.5 bg-gray-100 dark:bg-slate-900/60 rounded-full overflow-hidden relative shadow-inner ring-1 ring-black/5 dark:ring-white/5 transition-colors duration-300">

            {/* Green Bar (Base) */}
            <div
              className="h-full bg-[#00c541] transition-all duration-500 ease-out shadow-[0_0_8px_rgba(0,197,65,0.4)] absolute top-0 left-0"
              style={{ width: `${greenPercentage}%` }}
            ></div>

            {/* Rainbow Bar (Masked Overlay) */}
            <div
              className="h-full transition-all duration-500 ease-out absolute top-0 left-0 overflow-hidden z-10"
              style={{ width: `${rainbowPercentage}%` }}
            >
              {/* Rainbow Gradient (Full Width of Container to avoid squashing) */}
              <div
                className="h-full"
                style={{
                  width: containerWidth ? `${containerWidth}px` : '100%',
                  background: 'linear-gradient(to right, #a855f7, #6366f1, #3b82f6, #22c55e, #eab308, #f97316, #ef4444)'
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const StatPanel = ({ stats, onStatChange }) => {
  const { t } = useTranslation();

  // Configuration matching the API stat names
  const statConfig = [
    { name: 'Speed', url: 'https://umacalculator.com/images/Icon/Stats_icons/Stat_Type_Speed.png' },
    { name: 'Stamina', url: 'https://umacalculator.com/images/Icon/Stats_icons/Stat_Type_Stamina.png' },
    { name: 'Power', url: 'https://umacalculator.com/images/Icon/Stats_icons/Stat_Type_Power.png' },
    { name: 'Guts', url: 'https://umacalculator.com/images/Icon/Stats_icons/Stat_Type_Guts.png' },
    { name: 'Wit', url: 'https://umacalculator.com/images/Icon/Stats_icons/Stat_Type_Wit.png' },
  ];

  // Calculate scores using the existing Game Logic
  const statScores = {};
  let totalStatScore = 0;

  statConfig.forEach(({ name }) => {
    const val = stats ? stats[name] : 0;
    const score = calculateStatScore(val || 0);
    statScores[name] = score;
    totalStatScore += score;
  });

  // Helper for Trophy Icon (Inline SVG to avoid dependency issues)
  const TrophyIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
      <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
      <path d="M4 22h16" />
      <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" />
      <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
      <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
    </svg>
  );

  return (
    <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl p-1 w-full border border-gray-100 dark:border-slate-700 transition-all duration-300 mb-6">

      {/* Inner Container */}
      <div className="p-4 sm:p-6 rounded-xl bg-gradient-to-b from-transparent to-gray-50/50 dark:to-slate-900/20">

        {/* Header */}
        <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-100 dark:border-slate-700/60 transition-colors duration-300">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[#00c541]/10 dark:bg-[#00c541]/20 rounded-lg text-[#00c541] dark:text-[#4ade80]">
              <TrophyIcon />
            </div>
            <div className="flex flex-col">
              <h3 className="text-xl font-bold text-gray-800 dark:text-white transition-colors duration-300">{t('StatPanel.title')}</h3>
              <span className="text-xs text-gray-400 dark:text-slate-400">{t('StatPanel.subtitle')}</span>
            </div>
          </div>
          <div className="px-3 py-1 bg-gray-100 dark:bg-slate-700/80 rounded text-xs font-mono font-semibold text-gray-500 dark:text-gray-300 border border-gray-200 dark:border-slate-600 transition-colors duration-300">
            {t('StatPanel.max_label')}
          </div>
        </div>

        {/* Main Stats Bar Group */}
        <div className="flex rounded-lg overflow-hidden shadow-lg border-2 border-[#00c541] dark:border-[#00a035] mb-8 transition-colors duration-300 ring-2 ring-[#00c541]/20 dark:ring-[#00a035]/20 ring-offset-2 dark:ring-offset-slate-800">
          {statConfig.map(({ name, url }, index) => (
            <StatItem
              key={name}
              name={name}
              iconUrl={url}
              value={stats ? stats[name] : 0}
              isLast={index === statConfig.length - 1}
              onStatChange={onStatChange}
              t={t}
            />
          ))}
        </div>

        {/* Score Breakdown Footer */}
        <div className="bg-gray-50 dark:bg-slate-900/40 rounded-xl p-5 border border-gray-200 dark:border-slate-700/60 transition-colors duration-300 relative overflow-hidden">

          {/* Background Decoration */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-[#00c541]/5 to-transparent rounded-bl-full pointer-events-none"></div>

          <h4 className="text-xs font-bold text-gray-400 dark:text-slate-500 uppercase tracking-wider mb-4 flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-gray-400 dark:bg-slate-500"></span> {t('StatPanel.score_breakdown')}
          </h4>

          <div className="grid grid-cols-5 gap-2 mb-6 relative z-10">
            {statConfig.map(({ name }) => (
              <div key={name} className="text-center group cursor-default">
                <div className="text-[10px] sm:text-xs text-gray-400 dark:text-slate-500 uppercase mb-1 group-hover:text-[#00c541] transition-colors">
                  {t(`StatPanel.stats.${name}`).substring(0, 3)}
                </div>
                <div className="font-mono font-bold text-gray-700 dark:text-gray-300 text-sm sm:text-base group-hover:scale-105 transition-transform">{statScores[name]}</div>
              </div>
            ))}
          </div>

          <div className="flex justify-between items-end pt-4 border-t border-gray-200 dark:border-slate-700/60 transition-colors duration-300">
            <span className="text-gray-500 dark:text-slate-400 font-medium text-sm">{t('StatPanel.total_evaluation')}</span>
            <div className="flex flex-col items-end">
              <div className="flex items-baseline gap-1.5">
                <span className="text-3xl sm:text-4xl font-black text-[#00c541] dark:text-[#4ade80] drop-shadow-sm transition-colors duration-300 leading-none">{totalStatScore}</span>
                <span className="text-xs text-gray-400 font-bold uppercase tracking-wide">{t('StatPanel.unit_pts')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatPanel;
// Revisado: 2025-12-18 I18n Updated
